import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import moment from 'moment';
import Modal from '../../components/Modal';

const BatchProtocolQuestionView = () => {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchHierarchy, setBatchHierarchy] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await api.get('/batches');
      if (response.data.success) {
        setBatches(response.data.batches || []);
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
      setError('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadBatchHierarchy = async (batchId) => {
    try {
      setLoadingHierarchy(true);
      setSelectedBatch(batchId);
      setSelectedProtocol(null);
      setBatchHierarchy(null);
      setError(null);
      
      const response = await api.get(`/reports/batch-protocol-question-hierarchy?batch_id=${batchId}`);
      if (response.data.success) {
        setBatchHierarchy(response.data.hierarchy);
      }
    } catch (err) {
      console.error('Failed to load batch hierarchy:', err);
      setError('Failed to load batch details');
    } finally {
      setLoadingHierarchy(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const renderBatchSelection = () => {
    if (loadingBatches) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading batches...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-layer-group me-2"></i>
                Select a Batch
              </h5>
              <p className="mb-0 mt-2 opacity-75">
                Choose a batch to view protocols and question-level time tracking data
              </p>
            </div>
            <div className="card-body">
              {batches.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-info-circle fa-3x text-dark mb-3"></i>
                  <p className="text-dark mb-0">No batches found. Create a batch first to view time tracking data.</p>
                </div>
              ) : (
                <div className="row">
                  {batches.map((batch) => (
                    <div key={batch.id} className="col-lg-4 col-md-6 mb-3">
                      <div 
                        className={`card h-100 border-2 ${selectedBatch === batch.id ? 'border-primary' : 'border-light'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => loadBatchHierarchy(batch.id)}
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0 fw-bold text-primary">
                              {batch.name}
                            </h6>
                            <span className={`badge bg-${batch.status === 'active' ? 'success' : 'secondary'}`}>
                              {batch.status}
                            </span>
                          </div>
                          {batch.description && (
                            <p className="text-dark small mb-3">{batch.description}</p>
                          )}
                          
                          <div className="row text-center small">
                            <div className="col-6">
                              <div className="text-dark">Start Date</div>
                              <div className="fw-bold">{formatDate(batch.start_date)}</div>
                            </div>
                            <div className="col-6">
                              <div className="text-dark">End Date</div>
                              <div className="fw-bold">{formatDate(batch.end_date)}</div>
                            </div>
                          </div>
                          
                          <div className="row text-center small mt-2">
                            <div className="col-4">
                              <div className="text-dark">Cases</div>
                              <div className="fw-bold">{batch.case_count || 0}</div>
                            </div>
                            <div className="col-4">
                              <div className="text-dark">Doctors</div>
                              <div className="fw-bold">{batch.doctor_count || 0}</div>
                            </div>
                            <div className="col-4">
                              <div className="text-dark">Time Left</div>
                              <div className="fw-bold text-warning">
                                {batch.time_remaining || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBatchHierarchy = () => {
    if (!batchHierarchy) return null;

    return (
      <div className="row">
        {/* Batch Summary */}
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">
                <i className="fas fa-calendar-alt me-2"></i>
                Batch: {batchHierarchy.batch_name}
              </h5>
              <p className="mb-0 mt-2 opacity-75">
                {batchHierarchy.batch_description || 'No description available'}
              </p>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3 mb-3">
                  <div className="text-primary mb-2">
                    <i className="fas fa-file-contract fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-primary">{batchHierarchy.batch_summary?.total_protocols}</h5>
                  <p className="text-dark mb-0 small">Total Protocols</p>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-success mb-2">
                    <i className="fas fa-question-circle fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-success">{batchHierarchy.batch_summary?.total_questions_answered}</h5>
                  <p className="text-dark mb-0 small">Questions Answered</p>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-warning mb-2">
                    <i className="fas fa-clock fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-warning">
                    {formatTime(batchHierarchy.batch_summary?.total_time_seconds)}
                  </h5>
                  <p className="text-dark mb-0 small">Total Time Spent</p>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-info mb-2">
                    <i className="fas fa-stopwatch fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-info">
                    {formatTime(batchHierarchy.batch_summary?.average_time_per_question)}
                  </h5>
                  <p className="text-dark mb-0 small">Avg Time per Question</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Protocols List */}
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Protocols in this Batch
              </h5>
            </div>
            <div className="card-body">
              {batchHierarchy.protocols.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-info-circle fa-3x text-dark mb-3"></i>
                  <p className="text-dark mb-0">No protocols found in this batch.</p>
                </div>
              ) : (
                <div className="row">
                  {batchHierarchy.protocols.map((protocol) => (
                    <div key={protocol.protocol_id} className="col-lg-6 mb-4">
                      <div 
                        className="card h-100 border-2 border-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedProtocol(protocol)}
                      >
                        <div className="card-header bg-primary text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold">{protocol.protocol_name}</h6>
                            <span className="badge bg-light text-primary">
                              {protocol.completion_percentage?.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="card-body">
                          {protocol.protocol_description && (
                            <p className="text-dark small mb-3">{protocol.protocol_description}</p>
                          )}
                          
                          <div className="row text-center small mb-3">
                            <div className="col-4">
                              <div className="text-dark">Questions</div>
                              <div className="fw-bold">
                                {protocol.total_questions_answered} / {protocol.total_questions_in_protocol}
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="text-dark">Time</div>
                              <div className="fw-bold">{formatTime(protocol.total_time_seconds)}</div>
                            </div>
                            <div className="col-4">
                              <div className="text-dark">Avg</div>
                              <div className="fw-bold">{formatTime(protocol.average_time_per_question)}</div>
                            </div>
                          </div>
                          
                          <div className="progress mb-2" style={{ height: '8px' }}>
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: `${protocol.completion_percentage}%` }}
                              aria-valuenow={protocol.completion_percentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                          
                          <div className="text-center">
                            <small className="text-dark">
                              Click to view question-level time tracking
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionsModal = () => {
    if (!selectedProtocol) return null;

    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">
                <i className="fas fa-question-circle me-2"></i>
                {selectedProtocol.protocol_name} - Question Time Tracking
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setSelectedProtocol(null)}></button>
            </div>
            <div className="modal-body">
              {selectedProtocol.questions_with_time_data.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-info-circle fa-3x text-dark mb-3"></i>
                  <p className="text-dark mb-0">No time tracking data available for questions in this protocol.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Question</th>
                        <th>Category</th>
                        <th>Times Answered</th>
                        <th>Total Time</th>
                        <th>Average Time</th>
                        <th>Doctors Who Answered</th>
                        <th>Cases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProtocol.questions_with_time_data.map((question) => (
                        <tr key={question.question_id}>
                          <td>
                            <div className="fw-bold">{question.question_text}</div>
                            <small className="text-dark">ID: {question.question_id}</small>
                          </td>
                          <td>
                            <span className="badge bg-secondary">{question.question_category}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-info">{question.times_answered}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-warning text-dark">
                              {question.formatted_time}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-success">
                              {question.formatted_average_time}
                            </span>
                          </td>
                          <td className="text-center">
                            {question.doctors_who_answered && question.doctors_who_answered.length > 0 ? (
                              <div>
                                {question.doctors_who_answered.map((doctor, idx) => (
                                  <span key={doctor.id} className="badge bg-primary me-1 mb-1">
                                    {doctor.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-secondary">{question.cases_answered?.length || 0}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedProtocol(null)}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                <i className="fas fa-print me-1"></i>
                Print Report
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ResearchLayout pageTitle="Batch Time Tracking">
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="mb-1">Batch Time Tracking</h3>
                <p className="mb-0 opacity-75">
                  View time spent by doctors on each question within batches and protocols
                </p>
              </div>
              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  setSelectedBatch(null);
                  setBatchHierarchy(null);
                  setSelectedProtocol(null);
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Batches
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {loadingHierarchy ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading batch details...</span>
            </div>
          </div>
        ) : !batchHierarchy ? (
          renderBatchSelection()
        ) : (
          renderBatchHierarchy()
        )}

        {renderQuestionsModal()}
      </div>
    </ResearchLayout>
  );
};

export default BatchProtocolQuestionView;